/**
 * Workstations feature — Zod schemas, the single source of truth for the wire shapes.
 *
 * The surface is the tenant admin's half of the Windows credential provider's DEVICE plane: which
 * machines are enrolled, which have never converged onto a served policy, the enrolment tokens that
 * let a machine join, and the policy those machines are handed. Every shape below is transcribed
 * from the Go handlers, not guessed:
 *
 *   backend/internal/workstation/admin_handler.go
 *   backend/internal/workstation/policy_admin_handler.go
 *
 *   GET  /workstations/                          → { workstations: [workstationResponse] }
 *   GET  /workstations/enrollment-tokens         → { tokens: [tokenResponse] }
 *   POST /workstations/enrollment-tokens         → tokenResponse + { token }  (the one-time secret)
 *   POST /workstations/enrollment-tokens/{id}/revoke → 204, or 409 not_pending
 *   GET  /workstations/policy                    → { policies, settable_paths, seed_schema }
 *   GET  /workstations/policy/effective/{id}     → the resolved document + provenance + suppressions
 *   GET  /workstations/policy/impact             → the fleet view
 *   GET  /workstations/events?correlation_id=&device_id= → the uploaded trail
 *   PUT  /workstations/policy                    → whole-patch write at tenant scope
 *   PUT  /workstations/policy/devices/{id}       → whole-patch write at device scope
 *
 * THE TENANT COMES FROM THE SESSION AND THE HOST. There is no {tenant_id} path segment on this
 * tree; the api-client's host rewriter is what maps the canonical platform shape onto it (see
 * queries.ts).
 *
 * TWO VOCABULARIES ARE DELIBERATELY OPEN HERE (z.string(), not z.enum): `assurance` and `status`.
 * Both are closed on the server today, and both are expected to GROW — assurance gains a value the
 * moment machine attestation (Rung 2) lands. A z.enum would turn that server-side addition into a
 * parse failure that blanks the whole roster, which is a worse answer than one row rendering an
 * unrecognised label. The label maps below fall back to the raw value for exactly that reason.
 *
 * WHAT THE API DOES NOT OFFER, recorded here rather than discovered by a reader looking for a
 * button:
 *   - NO DISABLE VERB. MountAdminManage carries token mint/revoke, the registry revoke, the two
 *     approve verbs and the two policy PUTs. There is no endpoint for the reversible `disabled`
 *     state, so `status` moves only between active and revoked from this surface, and
 *     `revoked_at` is a read-only fact here. (This entry claimed there was no registry revoke at
 *     all long after `POST /{device_id}/revoke` shipped — corrected with the approve verbs.)
 *   - RE-REGISTER IS DEFERRED. A machine whose TPM was cleared is handled by minting a fresh
 *     token and enrolling again. Rung 3 (`admin_approved`) DOES have an endpoint now — the two
 *     approve mutations in queries.ts — so this list no longer disclaims it.
 *   - NO GROUP SCOPE. It is modelled in the schema and the resolver, and the API answers 501
 *     `scope_not_resolvable` for it because nothing records which machines belong to a group. This
 *     surface therefore has tenant and device scopes only.
 *   - NO TRUNCATION FLAG ON THE ROSTER. `ListWorkstations` is `LIMIT 500` with no `truncated` in
 *     the response, unlike the impact endpoint which reports its own. `rosterMayBeTruncated` below
 *     is what this surface can honestly say instead.
 */

import { z } from "zod";

// ── List state, which lives in the URL ─────────────────────────────────────

/**
 * Roster filter.
 *
 * `never_converged` is first among equals and is the reason this filter exists at all: a machine
 * that has never fetched a policy document is running on whatever its installer seeded, and from
 * every other surface in the product it looks exactly like a working machine.
 */
export const workstationFilterSchema = z.enum([
  "all",
  "never_converged",
  "self_enrolled",
  "software_key",
  "not_active",
]);
export type WorkstationFilter = z.infer<typeof workstationFilterSchema>;

export const workstationsSearchSchema = z.object({
  /**
   * Narrow the roster to the machines ONE person's sign-ins authorised. A user id, not a name: the
   * value comes from a roster cell and goes back to the server as-is, and it must survive a person
   * being renamed or deprovisioned — which is exactly when this question gets asked.
   */
  authorised_by: z.string().optional(),
  /** Roster filter. Optional so a bare /workstations URL is canonical. */
  status: workstationFilterSchema.optional(),
  /**
   * The machine whose effective policy is open. A drill-in that is a shareable link — "this is the
   * machine that is not taking the lockdown, and here is why" is a sentence an operator needs to be
   * able to paste into a ticket.
   */
  device: z.string().optional(),
  /**
   * A correlation GUID, filtering the reported trail. It is the query that feature exists for — an
   * operator arrives holding this value out of a machine's own log.
   */
  correlation: z.string().optional(),
});
export type WorkstationsSearch = z.infer<typeof workstationsSearchSchema>;

// ── The roster ────────────────────────────────────────────────────────────

export const workstationSchema = z.object({
  device_id: z.string(),
  /** RFC 7638 thumbprint of the device key, 43 characters of unpadded base64url. THE identity. */
  jkt: z.string(),
  /** Which authorisation enrolled the machine. See ASSURANCE_LABEL — open vocabulary. */
  assurance: z.string(),
  /** The rung number behind `assurance`. Carried for completeness; the surface speaks assurance. */
  enrol_rung: z.number(),
  enrolled_at: z.string(),
  /**
   * Where the device key lives — `tpm` or `software` — AS THE CLIENT REPORTED IT at enrolment.
   * Nothing has verified the claim (machine attestation is deferred), which is why the field keeps
   * its `_reported` suffix all the way onto this surface.
   */
  key_protection_reported: z.string(),
  /**
   * The machine's own name for itself. A LABEL FOR HUMANS, NEVER AN IDENTIFIER: self-reported,
   * unverified, and not unique. Omitted entirely when the machine never reported one, which is
   * honest — a placeholder would read as though the platform had been told.
   */
  hostname_reported: z.string().optional(),
  /**
   * The user whose completed sign-in authorised this machine's enrolment. Present only on a machine
   * that enrolled itself (Rung 0); absent on every token-enrolled one, which is honest — nobody
   * authorised those with a ceremony.
   *
   * AN AUDIT FACT AND NEVER AN OWNER. The column is "Authorised by", never "Owner": nothing treats
   * this person as privileged on this machine, and deprovisioning them does not de-enrol it. It is
   * here because bound 4's incident signal is ONE PERSON, MANY MACHINES, and a fact nobody can group
   * by does not surface that.
   */
  enrolled_by_user_id: z.string().optional(),
  /**
   * active | disabled | revoked. `revoked` is TERMINAL and is now reachable from this console:
   * the registry revoke disables the thumbprint immediately. There is no un-revoke —
   * a machine that must come back does so as a new enrolment on a new device identity.
   */
  status: z.string(),
  revoked_at: z.string().optional(),
  /**
   * ABSENT MEANS NEVER CONVERGED, and that is the single most valuable read on this page. The
   * machine is running on its installer seed and has never received a signed document.
   */
  last_config_fetch_at: z.string().optional(),
  /** The monotonic per-device revision the client refuses to go backwards behind. */
  policy_revision: z.number(),
  /** Server time at which PureID issued this machine's latest attributed logon certificate. */
  last_authentication_at: z.string().optional(),
  /** User authenticated for that issuance. This is not an assertion that LSA created a session. */
  last_authentication_user_id: z.string().optional(),
});
export type Workstation = z.infer<typeof workstationSchema>;

/**
 * A DISPLAY-ONLY resolved principal label, carried on the roster's `principals` sidecar keyed by
 * `enrolled_by_user_id`. The id stays the fact of record; this is a convenience label.
 *
 * Three outcomes and they stay distinct: a `name` resolved; `deleted: true` is an honest tombstone
 * for a purged user; an id ABSENT from the map is UNRESOLVED, which the console renders as a mono
 * short id. The whole map is omitted when the lookup faults, so "we could not resolve this" never
 * renders as "this user was deleted".
 */
export const principalLabelSchema = z.object({
  name: z.string().optional(),
  deleted: z.boolean().optional(),
});
export type PrincipalLabel = z.infer<typeof principalLabelSchema>;

export const workstationsResponseSchema = z.object({
  workstations: z.array(workstationSchema),
  /** Keyed by user id. Absent when there was nothing to resolve, or the resolution faulted. */
  principals: z.record(z.string(), principalLabelSchema).optional(),
});
export type WorkstationsResponse = z.infer<typeof workstationsResponseSchema>;

/** The status a revoked machine holds. Terminal — nothing moves it back. */
export const STATUS_REVOKED = "revoked";

/**
 * What the registry revoke answers.
 *
 * `changed: false` is not a failure. It says the machine was ALREADY cut off, so this call withdrew
 * nothing — which is exactly what an operator retrying after a timeout wanted. The console words
 * its confirmation differently for the two, and treats neither as an error.
 */
export const revokedWorkstationSchema = z.object({
  device_id: z.string(),
  jkt: z.string(),
  status: z.string(),
  revoked_at: z.string(),
  changed: z.boolean(),
});
export type RevokedWorkstation = z.infer<typeof revokedWorkstationSchema>;

/**
 * Admitting a machine that enrolled itself.
 *
 * WHY THE OUTCOME IS A STRING AND NOT A BOOLEAN. A bulk approval over a roster selection
 * legitimately mixes results: some machines were pending, some an admin already approved, some were
 * never pending (a token machine was admitted at mint time), some are cut off, and some ids are
 * stale. Every one of those is a 200 — collapsing them to succeeded/failed would make a partially
 * stale selection look like a failed request and invite a retry of something that already worked.
 *
 * OPEN VOCABULARY, matching this file's assurance handling exactly: an unrecognised outcome renders
 * as itself rather than blanking the dialog, so a server that grows a sixth outcome degrades to a
 * readable word instead of a parse failure that loses the other 499 results.
 */
export const APPROVAL_OUTCOME_LABEL: Record<string, string> = {
  approved: "Approved",
  already_approved: "Already approved",
  not_pending: "Nothing to approve",
  not_active: "Cut off",
  unknown: "No longer in the roster",
};

export function approvalOutcomeLabel(outcome: string): string {
  return APPROVAL_OUTCOME_LABEL[outcome] ?? outcome;
}

export const approveResultSchema = z.object({
  device_id: z.string(),
  jkt: z.string().optional(),
  hostname_reported: z.string().nullable().optional(),
  outcome: z.string(),
  prior_assurance: z.string().optional(),
  changed: z.boolean(),
});
export type ApproveResult = z.infer<typeof approveResultSchema>;

/** The single-machine response, which additionally reports the assurance the row now carries. */
export const approvedWorkstationSchema = approveResultSchema.extend({
  assurance: z.string(),
});
export type ApprovedWorkstation = z.infer<typeof approvedWorkstationSchema>;

/**
 * The bulk response. `approved` counts ONLY the machines this call actually promoted — never the
 * already-approved ones — so a retry reports a number that really happened.
 */
export const approveWorkstationsSchema = z.object({
  approved: z.number(),
  counts: z.record(z.string(), z.number()),
  results: z.array(approveResultSchema).default([]),
});
export type ApproveWorkstationsResult = z.infer<typeof approveWorkstationsSchema>;

/** The server refuses more than this in one call; the console must not offer a larger selection. */
export const APPROVAL_BATCH_LIMIT = 500;

/**
 * The server's roster query is `ORDER BY enrolled_at DESC LIMIT 500` and returns no truncation
 * flag — unlike the impact endpoint, which reports its own. So the honest thing this surface can
 * say is "the answer may be of the 500 most recently enrolled", and it says it only when the count
 * is exactly at the ceiling. A fleet of exactly 500 machines gets one over-cautious sentence; a
 * fleet of 900 does not get a silently wrong convergence count.
 */
export const ROSTER_LIMIT = 500;
export function rosterMayBeTruncated(rows: readonly Workstation[]): boolean {
  return rows.length >= ROSTER_LIMIT;
}

/**
 * A machine is waiting for an admin exactly when it enrolled itself and is still live.
 *
 * DERIVED HERE RATHER THAN ASKED OF THE SERVER. The roster already carries both fields, and a
 * second source for "is this pending?" is a second thing that can disagree with the resolver — which
 * tests the same `assurance` value to decide whether to shield the machine. One predicate, read by
 * the queue and by the row badge alike.
 *
 * A REVOKED SELF-ENROLLED MACHINE IS NOT PENDING, and that is not a detail: approving it would
 * promote a machine an admin deliberately cut off, and the server refuses it as `not_active`. The
 * console must not offer the action it knows will be refused.
 */
export function isPendingApproval(w: Workstation): boolean {
  return w.assurance === ASSURANCE_SELF_ENROLLED && w.status === "active";
}

/**
 * Which authorisation enrolled a machine. Sentence case, no internal jargon: the design calls
 * these "rungs" and the console never does.
 *
 * `self_enrolled` is the one an operator must be able to pick out at a glance, because it is the
 * assurance the two post-resolution shields key on — such a machine can never receive
 * `exclusiveMode: true` or `fingerprintOnly`, whatever the tenant policy says.
 */
export const ASSURANCE_LABEL: Record<string, string> = {
  token: "Enrolment token",
  token_batch: "Imaging batch",
  self_enrolled: "User sign-in",
  attested: "Machine attestation",
  admin_approved: "Admin approved",
};

export const ASSURANCE_NOTE: Record<string, string> = {
  token: "An admin minted a single-use token bound to this machine's key.",
  token_batch:
    "Enrolled from a shared imaging token. Whoever held the image could have enrolled it.",
  self_enrolled:
    "One user's sign-in on this machine authorised the enrolment. Lockdown settings are withheld until an admin approves it.",
  attested: "The machine's own hardware attested to its identity.",
  admin_approved:
    "An admin admitted this machine. Settings that can lock a user out of it now apply to it.",
};

/** Falls back to the raw value: an unrecognised assurance must render as itself, never as blank. */
export function assuranceLabel(assurance: string): string {
  return ASSURANCE_LABEL[assurance] ?? assurance;
}

/** The value the two post-resolution shields test against. */
export const ASSURANCE_SELF_ENROLLED = "self_enrolled";

export const KEY_PROTECTION_LABEL: Record<string, string> = {
  tpm: "TPM",
  software: "Software",
};

export function keyProtectionLabel(protection: string): string {
  return KEY_PROTECTION_LABEL[protection] ?? protection;
}

/** The name to show for a machine. The thumbprint when it never told us one. */
export function workstationLabel(machine: Workstation): string {
  return machine.hostname_reported ?? machine.jkt;
}

/** Has this machine ever received a signed policy document? */
export function hasConverged(machine: Workstation): boolean {
  return machine.last_config_fetch_at !== undefined;
}

/**
 * Is this machine one that person's sign-in authorised?
 *
 * A SEPARATE PREDICATE FROM THE SEGMENTED FILTER, deliberately: the two compose. "Every machine this
 * person enrolled, of which the ones running on their installer seed" is the incident question, and
 * folding this into the single-choice filter vocabulary would make it unaskable.
 */
export function matchesAuthorisedBy(machine: Workstation, userID: string | undefined): boolean {
  if (!userID) return true;
  return machine.enrolled_by_user_id === userID;
}

export function matchesFilter(machine: Workstation, filter: WorkstationFilter): boolean {
  switch (filter) {
    case "all":
      return true;
    case "never_converged":
      return !hasConverged(machine);
    case "self_enrolled":
      return machine.assurance === ASSURANCE_SELF_ENROLLED;
    case "software_key":
      return machine.key_protection_reported === "software";
    case "not_active":
      return machine.status !== "active";
  }
}

/**
 * The counts the convergence panel leads with, derived in exactly one place.
 *
 * `neverConverged` counts ACTIVE machines only. A revoked machine that never converged is not an
 * operational problem to chase — it is a row that will never converge by design — and counting it
 * beside the machines that should be fetching policy and are not would inflate the one number this
 * page exists to make true.
 */
export interface FleetCounts {
  total: number;
  active: number;
  neverConverged: number;
  selfEnrolled: number;
  softwareKey: number;
}

export function fleetCounts(rows: readonly Workstation[]): FleetCounts {
  let active = 0;
  let neverConverged = 0;
  let selfEnrolled = 0;
  let softwareKey = 0;
  for (const machine of rows) {
    const isActive = machine.status === "active";
    if (isActive) active += 1;
    if (isActive && !hasConverged(machine)) neverConverged += 1;
    if (machine.assurance === ASSURANCE_SELF_ENROLLED) selfEnrolled += 1;
    if (machine.key_protection_reported === "software") softwareKey += 1;
  }
  return { total: rows.length, active, neverConverged, selfEnrolled, softwareKey };
}

// ── Enrolment tokens ──────────────────────────────────────────────────────

export const enrollmentTokenSchema = z.object({
  id: z.string(),
  /** The first characters of the token, kept so a roster row is recognisable. Never the secret. */
  display_prefix: z.string(),
  /**
   * The device-key thumbprint this token may enrol. Absent (omitempty on the wire) means the BATCH
   * variant, which is unbound by necessity — a token that ships before the machine exists cannot be
   * bound to a key that does not exist yet.
   */
  jkt: z.string().optional(),
  /** How many machines this token may enrol. 1 for a bound token, ≥2 for a batch. */
  device_cap: z.number(),
  redeemed_count: z.number(),
  /**
   * pending | redeemed | revoked | expired.
   *
   * `expired` IS DERIVED BY THE DATABASE CLOCK and never stored — the same clock the redemption
   * path compares `expires_at > now()` against. So this surface renders the server's word rather
   * than recomputing expiry from `expires_at` against the browser's clock, which is the one way a
   * roster could show as live a token its own door already refuses.
   */
  status: z.string(),
  created_at: z.string(),
  expires_at: z.string(),
  redeemed_at: z.string().optional(),
  revoked_at: z.string().optional(),
});
export type EnrollmentToken = z.infer<typeof enrollmentTokenSchema>;

export const enrollmentTokensResponseSchema = z.object({
  tokens: z.array(enrollmentTokenSchema),
});
export type EnrollmentTokensResponse = z.infer<typeof enrollmentTokensResponseSchema>;

/**
 * The mint response. `token` is the raw secret and exists exactly once, in this promise's value.
 * It is never written to a cache, never logged, and cannot be re-read — only its hash is stored.
 */
export const mintedTokenSchema = enrollmentTokenSchema.extend({
  token: z.string(),
});
export type MintedToken = z.infer<typeof mintedTokenSchema>;

/** Is this token still redeemable? Only `pending` is; the other three are terminal. */
export function isTokenPending(token: EnrollmentToken): boolean {
  return token.status === "pending";
}

// ── The mint form ─────────────────────────────────────────────────────────

/**
 * An RFC 7638 thumbprint: 43 characters of unpadded base64url. Mirrors the server's
 * `isRFC7638Thumbprint`, which is deliberate rather than duplicative — the server refuses a
 * mistyped value either way, and catching it in the form is the difference between an admin fixing
 * a paste and an admin reading a 400.
 *
 * The commonest wrong paste is a 64-character hex SPKI hash, which is what the GATEWAY surface
 * asks for. It binds nothing here and presents later as a machine that simply cannot enrol, so the
 * message names it.
 */
const JKT = /^[A-Za-z0-9_-]{43}$/;

/**
 * Lifetime as a closed set rather than a number, so the 7-day ceiling the server enforces cannot be
 * typed past in the form, and so the field needs no coercion (coercion widens the input type and
 * breaks react-hook-form's resolver generic).
 */
export const tokenLifetimeSchema = z.enum(["1h", "24h", "7d"]);
export type TokenLifetime = z.infer<typeof tokenLifetimeSchema>;

export const TOKEN_LIFETIME_SECONDS: Record<TokenLifetime, number> = {
  "1h": 60 * 60,
  "24h": 24 * 60 * 60,
  "7d": 7 * 24 * 60 * 60,
};

/** The server's ceiling on a batch token's device cap. Mirrors MaxBatchDeviceCap. */
export const MAX_BATCH_DEVICE_CAP = 1000;

/**
 * How this token may be redeemed, as a DISCRIMINATED UNION rather than a checkbox beside two
 * optional fields — because the two branches are different acts and the server refuses any mix of
 * them (a thumbprint and `batch` cannot both be stated; a batch needs a cap of at least 2; a bound
 * token may not carry a cap above 1). Making it the discriminant means an unbound mint cannot
 * smuggle a thumbprint and a bound one cannot omit it, in the type as well as at runtime.
 *
 * No `.default()` anywhere in here: Zod 4 widens the input type to undefined-allowed and breaks
 * react-hook-form's SubmitHandler generic. Defaults live in the form's defaultValues.
 */
export const mintTokenFormSchema = z.discriminatedUnion("mode", [
  z.object({
    mode: z.literal("bound"),
    jkt: z
      .string()
      .regex(
        JKT,
        "43 characters of unpadded base64url. The machine prints it after it generates its device key — a 64-character hex fingerprint is the Edge deployments value and binds nothing here.",
      ),
    lifetime: tokenLifetimeSchema,
  }),
  z.object({
    mode: z.literal("batch"),
    device_cap: z
      .number({ message: "Enter how many machines this image will enrol." })
      .int("Whole machines only.")
      .min(2, "A batch needs at least 2. For a single machine, bind the token to its key instead.")
      .max(MAX_BATCH_DEVICE_CAP, `The ceiling is ${MAX_BATCH_DEVICE_CAP}.`),
    lifetime: tokenLifetimeSchema,
  }),
]);
export type MintTokenForm = z.infer<typeof mintTokenFormSchema>;

// ── Policy: the stored overrides ──────────────────────────────────────────

/**
 * The three modelled scopes. `group` is real in the schema and in the resolver and is REFUSED by
 * the write path with 501 `scope_not_resolvable` — nothing records which machines belong to a
 * group, so a row would apply to nobody while appearing to apply to some. This surface therefore
 * offers tenant and device only, and never renders a group control; it can still READ a group value
 * in provenance, which is why the layer labels below carry one.
 */
export const POLICY_SCOPE_TENANT = "tenant";
export const POLICY_SCOPE_DEVICE = "device";

export const policyRowSchema = z.object({
  scope: z.string(),
  /** The device this row is for, on a device-scope row. Absent at tenant scope. */
  scope_ref: z.string().optional(),
  /**
   * The SPARSE OVERRIDE: dotted document path → value. Presence is the override, absence is
   * "unset", and that distinction is the whole point — a plain per-setting value could not express
   * it, and the resulting bug is an admin setting one field and silently resetting every other.
   */
  patch: z.record(z.string(), z.unknown()),
  updated_at: z.string(),
  updated_by: z.string(),
});
export type PolicyRow = z.infer<typeof policyRowSchema>;

/**
 * `ListPolicies` is `ORDER BY (tenant, group, device), updated_at DESC LIMIT 500`, and returns no
 * truncation flag.
 *
 * The ordering saves the tenant row — scope 'tenant' sorts first, so it is always in the answer.
 * DEVICE rows are what fall out, oldest-edited first, and that is dangerous in a specific way:
 * a machine whose override is outside the window reads as having none, while the effective endpoint
 * (which resolves the real stored patch) goes on reporting its fields as coming from "this machine".
 * A whole-patch save from a form seeded with the empty patch would then clear an override the form
 * never showed. So this is not a display caveat — it is the gate on device-scope editing.
 */
export const POLICY_LIST_LIMIT = 500;
export function policyListMayBeTruncated(rows: readonly PolicyRow[]): boolean {
  return rows.length >= POLICY_LIST_LIMIT;
}

// ── The installer seed's published vocabulary ─────────────────────────────

/**
 * One field an installer seed may carry, as the server publishes it.
 *
 * DERIVED, NOT DECLARED. The server computes this from the same settable-field registry that decides
 * `settable_paths`, so the seed vocabulary is a strict subset of the tenant vocabulary by
 * construction rather than by agreement. That is exactly why this console renders it
 * instead of documenting it: a hardcoded copy here would be the drift the derivation exists to
 * prevent, one layer down.
 *
 * `kind` is an OPEN string for the same reason `assurance` is: it is the server's own wire vocabulary
 * and may grow. SEED_KIND_LABEL falls back to the raw word, so an unrecognised kind reads as itself
 * rather than blanking the field it describes.
 */
export const seedFieldSchema = z.object({
  /** The tenant policy path. Identical to the seed path minus the prefix — the same registry key. */
  path: z.string(),
  /** Where it sits in a seed document: `policy.` + path. The spelling an installer author types. */
  seedPath: z.string(),
  kind: z.string(),
  /** A seed WITHOUT this field is invalid. Exactly one field is (the address the machine calls). */
  required: z.boolean().optional(),
  /** The closed set the SEED may use. Absent means the field's vocabulary is open. */
  vocabulary: z.array(z.string()).optional(),
  /** Present ONLY where the tenant policy's set is wider — the narrowing, shown as a narrowing. */
  tenantVocabulary: z.array(z.string()).optional(),
  excludedValues: z.array(z.string()).optional(),
  /** Why those values are tenant-only. The reason is the product; it is rendered, never summarised. */
  excludedBecause: z.string().optional(),
  maxChars: z.number().optional(),
  maxValue: z.number().optional(),
  maxEntries: z.number().optional(),
  /** The tenant document treats JSON null as a value here and a seed may not. Only the proxy. */
  tenantNullable: z.boolean().optional(),
});
export type SeedField = z.infer<typeof seedFieldSchema>;

/**
 * A tenant-policy field the seed may not carry, and WHY.
 *
 * `refused` separates the two kinds, and the difference is real on the machine as well as here: a
 * refused field is rejected by name by the client's own seed parser, while a merely-not-carried one
 * is an unknown key. An admin asking "why can I not seed the kill switch?" is answered by `because`,
 * which is the whole reason this half of the artifact is published at all.
 */
export const seedExclusionSchema = z.object({
  path: z.string(),
  seedPath: z.string(),
  refused: z.boolean(),
  because: z.string(),
});
export type SeedExclusion = z.infer<typeof seedExclusionSchema>;

export const seedSchemaSchema = z.object({
  /** The seed document's own `version`, and the only one the client's seed parser accepts. */
  seedSchemaVersion: z.number(),
  /** The seed's CLOSED top-level key set. `policy` is the only one the fields below live under. */
  topLevelKeys: z.array(z.string()),
  policyPathPrefix: z.string(),
  fields: z.array(seedFieldSchema),
  excluded: z.array(seedExclusionSchema),
});
export type SeedSchema = z.infer<typeof seedSchemaSchema>;

/**
 * How a seed field's wire type reads on this surface. Open, falling back to the raw kind.
 *
 * "Text" rather than "string": the reader is an admin writing a JSON file, and the JSON type is
 * visible in the file they are writing. What they need from this column is which shape of value the
 * field takes.
 */
export const SEED_KIND_LABEL: Record<string, string> = {
  bool: "True or false",
  string: "Text",
  uint32: "Whole number",
  "string[]": "List of text",
};

export function seedKindLabel(kind: string): string {
  return SEED_KIND_LABEL[kind] ?? kind;
}

/**
 * The exclusions, split the way the two groups differ.
 *
 * Refused first: those are the ones an installer author is most likely to have already tried, and
 * the ones whose absence would otherwise look like an oversight rather than a decision.
 */
export function splitSeedExclusions(exclusions: readonly SeedExclusion[]): {
  refused: SeedExclusion[];
  notCarried: SeedExclusion[];
} {
  const refused: SeedExclusion[] = [];
  const notCarried: SeedExclusion[] = [];
  for (const exclusion of exclusions) {
    (exclusion.refused ? refused : notCarried).push(exclusion);
  }
  return { refused, notCarried };
}

export const policyListResponseSchema = z.object({
  policies: z.array(policyRowSchema),
  /**
   * THE SETTABLE VOCABULARY, SERVED. The registry in the Go patch validator is the single source of
   * truth for what may be set; this surface renders from this list rather than keeping a second
   * copy that drifts. See policy-fields.ts for how a path with no local presentation is handled —
   * it is shown and refused, never silently dropped.
   */
  settable_paths: z.array(z.string()),
  /**
   * THE INSTALLER SEED'S VOCABULARY, SERVED BESIDE IT and derived from the same registry, so the two
   * cannot disagree about what a field is called or which values it takes.
   *
   * OPTIONAL, deliberately — for the reason the open vocabularies above are open. This one read backs
   * the tenant policy card AND the editor, so a server that stopped sending this key (or sent a shape
   * this parse does not recognise) would take the whole policy surface down with it to hide one
   * reference panel. Absent means the panel is absent; nothing else on the page changes.
   */
  seed_schema: seedSchemaSchema.optional(),
});
export type PolicyListResponse = z.infer<typeof policyListResponseSchema>;


// ── Policy: the resolved document ─────────────────────────────────────────

/**
 * The flat, already-resolved document one machine is served. The client never sees a layer, for
 * three reasons the design gives in cost order: a policy-merge bug at a Windows logon screen is a
 * lockout; the signature covers ONE document, so there must be one document; and "effective policy
 * for this device" has to be a single value both sides can name, or an incident becomes an argument
 * about which layer was winning.
 */
export const policyDocumentSchema = z.object({
  exclusiveMode: z.boolean(),
  environment: z.object({
    onPremAd: z.boolean(),
    hybridEntra: z.boolean(),
    entraOnly: z.boolean(),
    rdp: z.boolean(),
  }),
  timeouts: z.object({
    authAttemptSeconds: z.number(),
    brokerCallMs: z.number(),
  }),
  lockdown: z.object({ enabled: z.boolean(), message: z.string() }),
  idp: z.object({
    baseUrl: z.string(),
    allowedOrigins: z.array(z.string()),
    /** null is how the document spells "no proxy" — a VALUE, not an absence. */
    proxy: z.string().nullable(),
  }),
  enrollment: z.object({ otpChannel: z.string(), atLogonScreen: z.boolean() }),
  logging: z.object({ level: z.string(), fileSink: z.boolean() }),
  userVerification: z.object({ method: z.string() }),
  deviceKey: z.object({ protection: z.string() }),
  // BOTH PATHS, AND THE SECOND IS NOT OPTIONAL POLISH. Zod strips unknown keys, so a path missing
  // from this object resolves to `undefined` at policyValueAtPath and renders as "Not set" beside a
  // provenance label that says Organisation — a stored value the panel claims is absent, which is the
  // exact failure the effective-policy panel exists to prevent. Every settable path the server serves
  // needs a home here.
  //
  // REQUIRED, NOT TOLERANT, AND THAT IS THE DELIBERATE TRADE. A `.catch(0)` or an optional field
  // would keep the panel rendering against a backend that does not serve this key — and would render
  // it as "Not set", which is the same wrong claim about a security setting, just arrived at quietly
  // instead of loudly. So a missing key fails the whole parse and the panel shows its error state.
  // The cost is a hard version coupling: the server must serve every key named here, which it does
  // unconditionally (the Go struct tags are not omitempty), so the coupling only bites a frontend
  // deployed ahead of its backend — a deployment fault, and one that should be loud.
  offline: z.object({ windowHours: z.number(), windowMinutes: z.number() }),
  allowedAuthenticators: z.array(z.string()),
});
export type PolicyDocument = z.infer<typeof policyDocumentSchema>;

/**
 * Read one dotted path out of a resolved document.
 *
 * The document is walked as plain data rather than switched on per path — the alternative is a second
 * hardcoded copy of the document shape, which is the thing the served vocabulary exists to avoid.
 *
 * BUT THE SCHEMA ABOVE IS STILL A GATE, and the walk cannot see past it: Zod strips keys the schema
 * does not name, so a newly-served path reaches this function as `undefined` and formats as "Not set"
 * however faithfully the walk is written. Adding a settable path to the server means adding it to
 * `policyDocumentSchema` in the same change.
 */
export function policyValueAtPath(doc: PolicyDocument, path: string): unknown {
  let cursor: unknown = doc;
  for (const segment of path.split(".")) {
    if (typeof cursor !== "object" || cursor === null) return undefined;
    cursor = (cursor as Record<string, unknown>)[segment];
  }
  return cursor;
}

/** Where a resolved field's value came from. Open string for the reason given on ASSURANCE_LABEL. */
export const POLICY_LAYER_LABEL: Record<string, string> = {
  builtin: "Built-in default",
  tenant: "Tenant default",
  group: "Group",
  device: "This machine",
};

export function policyLayerLabel(layer: string): string {
  return POLICY_LAYER_LABEL[layer] ?? layer;
}

/**
 * One post-resolution filter that fired: what was asked for, what is actually being served, and
 * why.
 *
 * BOTH VALUES TRAVEL, and the reason is the effective-policy disclosure rule: "An admin believing a lockdown
 * is in force when it is not is the most dangerous wrong belief about a security control." An
 * endpoint returning only the clamped value would produce exactly that belief in every console
 * built on it, so this surface renders both and names the layer that asked.
 */
export const suppressionSchema = z.object({
  path: z.string(),
  /** Enumerated; the console renders its own words from this reason value. Today: `self_enrolled`. */
  reason: z.string(),
  requested_value: z.unknown(),
  effective_value: z.unknown(),
  /** The layer that asked for the suppressed value — the row an admin would edit to stop asking. */
  requested_from: z.string(),
});
export type Suppression = z.infer<typeof suppressionSchema>;

export const SUPPRESSION_REASON_LABEL: Record<string, string> = {
  self_enrolled: "Enrolled by a user sign-in",
};

export const SUPPRESSION_REASON_DETAIL: Record<string, string> = {
  // NAMES THE REMEDY THAT SCALES. This used to end "enrol it with an admin token instead", which is
  // only actionable one machine at a time — and Rung 0 is the zero-touch path, so it is how a fleet
  // enrols. That wording turned a bound meant to say "an admin has not admitted this machine yet"
  // into "this setting is unreachable for your whole estate". Approving the machine is the
  // documented promotion (implementation Rung 3) and it works over a selection.
  self_enrolled:
    "This machine was enrolled on the authority of one person's sign-in, so it is not yet handed a setting that can lock a user out of it. Approve the machine to admit it — you can approve a whole selection at once — and this setting takes effect on its next policy fetch.",
};

/**
 * A condition an admin should see and the resolver did NOT act on. Warn-and-allow: business and
 * security needs may require forcing the fingerprint, so a capability a machine has not reported is
 * a warning with counts, never a refusal.
 */
export const policyWarningSchema = z.object({
  path: z.string(),
  code: z.string(),
  detail: z.string(),
});
export type PolicyWarning = z.infer<typeof policyWarningSchema>;

export const effectivePolicySchema = z.object({
  device_id: z.string(),
  assurance: z.string(),
  policy: policyDocumentSchema,
  /** Settable path → the layer that won it. Paths no layer set read `builtin`. */
  provenance: z.record(z.string(), z.string()),
  suppressions: z.array(suppressionSchema),
  warnings: z.array(policyWarningSchema),
});
export type EffectivePolicy = z.infer<typeof effectivePolicySchema>;

// ── Policy: the fleet view ────────────────────────────────────────────────

export const policyImpactSchema = z.object({
  devices_counted: z.number(),
  devices_with_override: z.number(),
  exclusive_mode_in_force: z.number(),
  shielded: z.array(
    z.object({
      device_id: z.string(),
      jkt: z.string(),
      assurance: z.string(),
      suppressions: z.array(suppressionSchema),
    }),
  ),
  uv_capability_warnings: z.array(
    z.object({
      device_id: z.string(),
      jkt: z.string(),
      warnings: z.array(policyWarningSchema),
    }),
  ),
  /** Machines whose stored patch no longer validates. They are being served 503s right now. */
  devices_with_invalid_patch: z.number(),
  /**
   * TRUE MEANS THE ANSWER IS "OF THE FIRST 500", NOT "OF THE FLEET". Rendering a count without
   * honouring this builds a lie, so every consumer of this shape must read it.
   */
  truncated: z.boolean(),
});
export type PolicyImpact = z.infer<typeof policyImpactSchema>;

// ── Policy: what a write did ──────────────────────────────────────────────

export const policyWriteResultSchema = z.object({
  scope: z.string(),
  scope_ref: z.string().nullable().optional(),
  /**
   * THE FIELDS THAT CHANGED, not the fields the request restated. "Who turned the lockdown off" is
   * a question about a REMOVED key, which a record of the new patch alone cannot answer.
   */
  fields_changed: z.array(z.string()),
  policy_revision: z.number(),
  affected_devices: z.number(),
});
export type PolicyWriteResult = z.infer<typeof policyWriteResultSchema>;

/**
 * The enumerated refusal reasons a patch write can carry, alongside the offending path. Programmed
 * against by code rather than by string-matching the sentence, which is why the server enumerates
 * them at all; the sentence travels beside the code and is shown too.
 */
export const PATCH_REASON_LABEL: Record<string, string> = {
  unknown_field: "That is not a setting this server knows.",
  wrong_type: "That value is the wrong type for this setting.",
  out_of_vocabulary: "That value is outside the set this setting allows.",
  out_of_range: "That value is outside the range the Windows client accepts.",
  too_many_fields: "The change names more settings than the document has.",
  not_an_object: "That setting is a group of settings, not a setting.",
};

/** A patch refusal, as it arrives in the 400 body. Parsed leniently — the status is the signal. */
export const patchRefusalSchema = z.object({
  error: z.string(),
  path: z.string().optional(),
  reason: z.string().optional(),
  detail: z.string().optional(),
});
export type PatchRefusal = z.infer<typeof patchRefusalSchema>;

/**
 * Read a patch refusal out of an unknown error body. Returns null when the body is not one, so a
 * caller falls back to its own copy rather than rendering a half-parsed refusal.
 */
export function patchRefusalOf(body: unknown): PatchRefusal | null {
  const parsed = patchRefusalSchema.safeParse(body);
  return parsed.success ? parsed.data : null;
}

// ── The trail the machines upload ─────────────────────────────────────────

/**
 * One event a machine reported about itself.
 *
 * THESE ARE CLAIMS BY AN AUTHENTICATED MACHINE, NOT FACTS THE PLATFORM OBSERVED. The API says so in
 * the payload rather than only in its documentation (`evidence: "reported_by_workstation"`), and
 * the tables are separate at the database for the same reason. A console that rendered these beside
 * platform audit rows without the distinction would present the two as the same evidence.
 */
export const workstationEventSchema = z.object({
  id: z.string(),
  device_id: z.string(),
  /** The client's per-attempt GUID. Required on ingest, because it is the whole point: it is what
   * joins a client event to a server decision without correlating timestamps across three trust
   * boundaries during an incident. */
  correlation_id: z.string(),
  event_code: z.string(),
  outcome: z.string(),
  /** The client's own scale, 1 (most severe) to 5 (least). Refused outside that range on ingest. */
  severity: z.number(),
  /**
   * THE CLIENT'S CLOCK, AND IT MAY BE WRONG. A machine at a lock screen may have no usable clock at
   * all, which is the normal case rather than the edge one — so this is a claim, and `received_at`
   * is the fact a timeline is ordered by. The server orders by `received_at` and this surface keeps
   * that order.
   */
  occurred_at_claimed: z.string(),
  received_at: z.string(),
  /** Opaque to the server: shapes and outcomes, never values. Bounded, never interpreted. */
  detail: z.unknown(),
});
export type WorkstationEvent = z.infer<typeof workstationEventSchema>;

export const workstationEventsResponseSchema = z.object({
  events: z.array(workstationEventSchema),
  /** Stated in the payload. Today always `reported_by_workstation`. */
  evidence: z.string(),
});
export type WorkstationEventsResponse = z.infer<typeof workstationEventsResponseSchema>;

/**
 * The trail's own page ceiling. `ListAuditEvents` defaults to 200 and reports no truncation flag,
 * so — like the roster — the honest thing a console can say is that the answer may be the newest
 * 200, and only when the count sits exactly on it.
 */
export const EVENT_LIMIT = 200;
export function eventsMayBeTruncated(rows: readonly WorkstationEvent[]): boolean {
  return rows.length >= EVENT_LIMIT;
}

/** A correlation GUID, validated before it is put in the URL — the server refuses anything else. */
const UUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
export function isCorrelationID(value: string): boolean {
  return UUID.test(value);
}
